// libs
import React, { FunctionComponent, useMemo } from 'react'

// components
import MasonryFeed from 'src/components/feed/layouts/masonry_feed/masonry_feed'
import GroupInfo from 'src/components/groups/group_info/group_info'
import NotAllowedToSeePostsCTA from 'src/components/groups/not_allowed_to_see_posts_cta/not_allowed_to_see_posts_cta'
import PostCreateBanner from 'src/components/post/create/create_banner'
import {
  useGroupJoinConfirmation
} from 'src/containers/smart_components/group_member_list/hooks/useGroupJoinConfirmation'
import FeedWrapper from 'src/containers/smart_components/list_wrapper/feed_wrapper'

// constants
import {
  FEED_POST_TYPE_PRIVATE_POST
} from 'src/constants/feed'

// helpers
import { useShowLoginModal, useShowRegistrationModal } from 'src/actions/app-state/app-state_hooks'

import PostCreate from 'src/containers/post/create'
import { textResources } from 'src/lang/de'

// graphql
import { GroupDetailFragmentFragment, useGlobalPermissions, useUser } from '@lopo-tech/lopo-api'
import { useSelectIsUserLoggedIn } from '../../../reducers/user/user_hooks'

const useGetRenderPostCreate = (isPostCreateOpen: boolean, group: GroupDetailFragmentFragment) => {
  const isLoggedIn = useSelectIsUserLoggedIn()
  const { data } = useUser({ skip: !isLoggedIn })

  const isIntermediate = data?.currentProfile?.__typename === 'IntermediateProfile'

  if (!group) {
    return (): JSX.Element => {
      return <div key="-1"/>
    }
  }

  return (): JSX.Element => (
    <React.Fragment key='-1'>
      <GroupInfo
        group={group}
        intermediateOrNotLoggedIn={isIntermediate || !isLoggedIn}
      />
      {group.ownGroupParticipation && group.ownGroupParticipation.confirmedAt && (
        <>
          <PostCreateBanner
            callToActionText={textResources.postCreate.createPostTitle}
            postType={FEED_POST_TYPE_PRIVATE_POST}
            featureBlocked={!group.ownGroupParticipation}
            tileSize
            forceWizard
          />
          <PostCreate
            open={isPostCreateOpen}
            postType={FEED_POST_TYPE_PRIVATE_POST}
            featureBlocked={!group.ownGroupParticipation}
            groupId={group.id}
            groupName={group.name}
            topicId={group.topics?.[0]?.id}
          />
        </>
      )}
    </React.Fragment>)
}

const NotAllowedToSeePosts: FunctionComponent<{ group: GroupDetailFragmentFragment; joinGroup?: (group: GroupDetailFragmentFragment) => Promise<void> }> = ({ group, joinGroup }) => {
  const dispatchShowLoginModal = useShowLoginModal()
  const dispatchShowRegistrationModal = useShowRegistrationModal()
  const { onGroupJoin, ConfirmationModal } = useGroupJoinConfirmation()
  const { canShowGroupsList } = useGlobalPermissions()

  return (
    <>
      <NotAllowedToSeePostsCTA
        canJoinGroups={canShowGroupsList}
        canJoinThisGroup={group?.canJoin?.value ?? false}
        group={group}
        dispatchShowLoginModal={dispatchShowLoginModal}
        dispatchShowRegistrationModal={dispatchShowRegistrationModal}
        joinGroup={onGroupJoin}
      />
      {ConfirmationModal && <ConfirmationModal />}
    </>
  )
}

interface IProps {
  loading: boolean,
  group: GroupDetailFragmentFragment,
  getNextPage: () => void,
  postCreateOpen: boolean
}

const GroupFeed: React.FC<IProps> = ({ loading, group, getNextPage, postCreateOpen }) => {
  const renderPostCreate = useGetRenderPostCreate(postCreateOpen, group)

  const extraElements: JSX.Element[] = useMemo(() => {
    return group.ownGroupParticipation
      ? []
      : [<NotAllowedToSeePosts key="not-allowed" group={group} />]
  }, [group])

  return (
    <FeedWrapper
      getNextPage={getNextPage}
      initialLoad={false}
    >
      <MasonryFeed
        extraElements={extraElements}
        items={group?.topics?.[0].contentItems?.items ?? []}
        isLoading={loading}
        renderPostCreate={renderPostCreate}
        showEmptyFeed={!!group?.canShowTopics?.value}
      />
    </FeedWrapper>
  )
}

GroupFeed.displayName = 'GroupFeed'

export default GroupFeed
